import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "app/services/abstract.service";
import { DocumentRangeAttributes } from "@albatrosdeveloper/ave-models-package";
import { ICreateDocumentRange } from "./interfaces/create-document-range.interface";
import { IUpdateDocumentRange } from "./interfaces/update-document-range.interface";
import { RequestOptions } from "app/common/interfaces/options.interface";
const apiUrl = env.API_DOCUMENT_URL;
const moduleName = "document_range";

@Injectable({
  providedIn: "root",
})
export class DocumentRangeService extends AbstractService<
  DocumentRangeAttributes,
  ICreateDocumentRange,
  IUpdateDocumentRange
> {

  /** The last filter used to load the list */
  currentFilter: CombinedFilter<DocumentRangeAttributes>;

  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Obtiene rangos de documento con paginación y filtros.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto con filtros base
   * - Con búsqueda: Dos objetos (OR lógico) - busca en 'code', 'emissionPoint.name'
   *
   * Cada objeto del OR debe incluir TODAS las condiciones base para mantener consistencia.
   *
   * @param options Pagination options
   * @param documentTypeIds Document type IDs filter
   * @param documentClassIds Document class IDs filter
   * @param emissionPointIds Emission point IDs filter
   * @param modeTree Mode for tree display (disables pagination when true)
   * @param status Status filter
   * @returns Paginated data
   *
   * Search fields: code, emissionPoint.name
   * Array filters: documentTypeIds, documentClassIds, emissionPointIds, status
   */
  findAllWithCount(
    options: IPagination<DocumentRangeAttributes>,
    documentTypeIds: string[],
    documentClassIds: string[],
    emissionPointIds: number[],
    modeTree: boolean,
    status?
  ): Observable<IPaginationResult<DocumentRangeAttributes>> {

    const hasSearch = !!options.search;

    /**
     * Construye las condiciones base que SIEMPRE deben aplicarse.
     * Incluye: _deleted=false, filtros de tipo, clase, punto emisión, estados.
     *
     * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
     */
    const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<DocumentRangeAttributes>[] => {
      const deletedFilter: FilterFn<DocumentRangeAttributes> = params.useOrWhere
        ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
        : where('_deleted', Ops.eq(false));   // Primer objeto del array

      return [
        deletedFilter,
        documentTypeIds && documentTypeIds.length > 0
          ? andWhere('documentType.code' as keyof DocumentRangeAttributes, Ops.in(...documentTypeIds))
          : noop(),
        documentClassIds && documentClassIds.length > 0
          ? andWhere('documentClass.code' as keyof DocumentRangeAttributes, Ops.in(...documentClassIds))
          : noop(),
        emissionPointIds && emissionPointIds.length > 0
          ? andWhere('emissionPoint.id' as keyof DocumentRangeAttributes, Ops.in(...emissionPointIds))
          : noop(),
        status && status.length > 0
          ? andWhere('statusId', Ops.in(...status))
          : noop()
      ];
    };

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: Condiciones base + búsqueda por CODE (o solo condiciones si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<DocumentRangeAttributes>[] = [
      ...buildBaseFilters({ useOrWhere: false }),  // false = usa 'where' (primer objeto)
      hasSearch ? andWhere("code", Ops.like(options.search!, "i")) : noop()
    ];

    // Objeto 2: Condiciones base + búsqueda por EMISSION POINT NAME (solo si hay búsqueda)
    const searchByEmissionPointQueryObject: FilterFn<DocumentRangeAttributes>[] = hasSearch ? [
      ...buildBaseFilters({ useOrWhere: true }),   // true = usa 'orWhere' (segundo objeto)
      andWhere("emissionPoint.name" as keyof DocumentRangeAttributes, Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<DocumentRangeAttributes>(
      // Expandimos ambos objetos en el buildQuery
      ...searchByCodeQueryObject,
      ...searchByEmissionPointQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "code",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      modeTree ? noop() : page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<DocumentRangeAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(typeDocument?): Observable<IRequestResult<DocumentRangeAttributes[]>> {
    const query = buildQuery<DocumentRangeAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      typeDocument ? andAllWhere("documentType.code" as keyof DocumentRangeAttributes, Ops.eq(typeDocument)) : noop(),
      select(["code", "email", "leftFormat", "rightFormat", "documentClass", "documentType", "upperLimit", "lowerLimit", "emissionPoint", "dateTimeEnd"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<DocumentRangeAttributes[]>>(API_URL);
  }

  findByFilter(filter: CombinedFilter<DocumentRangeAttributes>, options?: RequestOptions) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
    return this.http.get<IPaginationResult<DocumentRangeAttributes>>(API_URL, { headers });
  }
}
